import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ShieldIcon, ZapIcon, DropletsIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface ServicePageProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ServicePageTemplate({
  setPage
}: ServicePageProps) {
  // In a real app, this data would be passed in via props or fetched based on a slug.
  // This template uses "Dustless Blasting" as the example content.
  const serviceData = {
    title: 'Dustless Blasting Services',
    subtitle: 'Low-mess, high-efficiency surface preparation for sensitive environments and tough coatings.',
    heroImage: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?q=80&w=1920&auto=format&fit=crop',
    overview: 'Our mobile dustless blasting equipment uses a mixture of water, air, and abrasive to remove surface contaminants and coatings. Adding water helps knock down airborne dust compared with open dry blasting, which often makes it a good fit for urban areas, active job sites, and projects with environmental requirements.',
    benefits: ['Low-mess dustless option', 'Can reduce the containment required on site', 'Water helps limit heat buildup on thin metal', 'EPA and DEQ conscious process', 'Leaves a clean, coating-ready surface', 'Workable around other trades on site'],
    applications: [{
      name: 'Automotive Restoration',
      desc: 'Removes paint, filler, and surface rust with media and pressure matched to the panel.'
    }, {
      name: 'Brick & Concrete',
      desc: 'Controlled surface prep to take off graffiti, old paint, and grime.'
    }, {
      name: 'Marine & Boats',
      desc: 'Strips bottom paint and gelcoat safely and efficiently.'
    }, {
      name: 'Industrial Equipment',
      desc: 'Cleans heavy machinery on-site with minimal disruption.'
    }]
  };
  return <div className="w-full bg-white" data-id="element-708" data-mp="16chuqwoxtiaw">
      {/* Hero Section */}
      <section className="relative min-h-[400px] md:min-h-[500px] flex items-center bg-charcoal" data-id="element-709" data-mp="23z711iqoah2k">
        <div className="absolute inset-0 z-0" data-id="element-710" data-mp="1yz2jdpe13gar">
          <img src={serviceData.heroImage} alt={serviceData.title} className="w-full h-full object-cover opacity-30" data-id="element-711" data-mp="1z0dsm4q8tg99" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-transparent" data-id="element-712" data-mp="1pxuprkmgj3n0"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full pt-20 pb-16" data-id="element-713" data-mp="imnk8ls3c5tt">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl" data-id="element-714" data-mp="eixys56pq4d0">
            <motion.div variants={fadeInUp} className="mb-4" data-id="element-715" data-mp="2u8694a62blot">
              <span className="text-brand-red font-heading font-bold uppercase tracking-widest text-sm" data-id="element-716" data-mp="2u6l9i64w5ck3">
                Core Service
              </span>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase leading-[1.1] mb-6" data-id="element-717" data-mp="34029jrvmfnw7">
              {serviceData.title}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-steel-200 font-body mb-8 leading-relaxed" data-id="element-718" data-mp="3hyicd7qd4u92">
              {serviceData.subtitle}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4" data-id="element-719" data-mp="1nl0ykcfrfbr8">
              <Button variant="primary" onClick={() => setPage('contact')} data-id="element-720" data-mp="1sj2eiohw2uyh">
                Get a Quote for this Service
              </Button>
              <Button variant="outline" href="tel:5177778088" showPhoneIcon data-id="element-721" data-mp="x7krggaznl3d">
                Call (517) 777-8088
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Overview & Benefits */}
      <section className="py-20 px-4" data-id="element-722" data-mp="1288qnp9df88o">
        <div className="max-w-7xl mx-auto" data-id="element-723" data-mp="129a605xf6gw7">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-724" data-mp="126e6we9u5m66">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-725" data-mp="eixmrywq4xqb">
              <h2 className="text-3xl md:text-4xl font-heading font-extrabold uppercase text-charcoal mb-6" data-id="element-726" data-mp="oaai50hqe613">
                The Smarter Way to Strip & Clean
              </h2>
              <p className="text-steel-600 text-lg leading-relaxed mb-8" data-id="element-727" data-mp="3rvpykzmggm75">
                {serviceData.overview}
              </p>

              <div className="space-y-4" data-id="element-728" data-mp="22gf9cw441tec">
                {serviceData.benefits.map((benefit, idx) => <motion.div key={idx} variants={fadeInUp} className="flex items-start" data-id="element-729" data-mp="276vn6hbufh18" data-mpi={idx}>
                    <CheckCircleIcon className="w-6 h-6 text-brand-red mr-3 flex-shrink-0 mt-0.5" data-id="element-730" data-mp="1sg69n8awtpu7" />
                    <span className="text-charcoal font-medium" data-id="element-731" data-mp="1xf9gmyaga64t">{benefit}</span>
                  </motion.div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative h-[400px] md:h-[500px] rounded-sm overflow-hidden shadow-xl" data-id="element-732" data-mp="2q4oml9n9d8tv">
              <img src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=1000&auto=format&fit=crop" alt="Dustless blasting in action" className="w-full h-full object-cover" data-id="element-733" data-mp="37flkaux0y8g6" />
              <div className="absolute bottom-0 left-0 right-0 bg-charcoal/90 backdrop-blur-sm p-6 border-t-4 border-brand-red" data-id="element-734" data-mp="2xnfspmaopt1i">
                <div className="flex items-center text-white" data-id="element-735" data-mp="2ookkk4o2y8vk">
                  <DropletsIcon className="w-8 h-8 text-brand-red mr-4" data-id="element-736" data-mp="2omrdwpdgvonk" />
                  <div data-id="element-737" data-mp="2joy7bv894smr">
                    <p className="font-heading font-bold uppercase text-lg" data-id="element-738" data-mp="2jruabxyyi7i2">
                      Low-Mess Option
                    </p>
                    <p className="text-steel-300 text-sm" data-id="element-739" data-mp="29s3bjk3d56c6">
                      Less airborne dust than open dry blasting
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Common Applications Grid */}
      <section className="py-20 bg-steel-50 px-4" data-id="element-740" data-mp="gg62uj49799q">
        <div className="max-w-7xl mx-auto" data-id="element-741" data-mp="gfeftwoq2jv2">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-742" data-mp="khr9vnu3wtc0">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-743" data-mp="u7ugiqu5i5st">
              Common Applications
            </h2>
            <p className="text-steel-600 max-w-2xl mx-auto" data-id="element-744" data-mp="1j6peemq0m7c">
              Dustless blasting is versatile enough to handle delicate
              restorations and aggressive enough for heavy industrial coatings.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-745" data-mp="bfrs2tb9pl80">
            {serviceData.applications.map((app, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-white p-8 border border-steel-200 hover:border-brand-red transition-colors group" data-id="element-746" data-mp="2400d67547eyf" data-mpi={idx}>
                <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3 group-hover:text-brand-red transition-colors" data-id="element-747" data-mp="2d06kfba7zip5">
                  {app.name}
                </h3>
                <p className="text-steel-600 leading-relaxed" data-id="element-748" data-mp="2mqrh2t6c2d5f">{app.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-red text-white text-center px-4" data-id="element-749" data-mp="2vr66o4qbyeeq">
        <div className="max-w-3xl mx-auto" data-id="element-750" data-mp="2vp3ilq8qmvcn">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold uppercase mb-6" data-id="element-751" data-mp="16d1btj3h9iu7">
            Need {serviceData.title} in Michigan?
          </h2>
          <p className="text-lg mb-8 text-white/90" data-id="element-752" data-mp="11f9lgtihe4s4">
            We are fully mobile and bring our equipment directly to your
            location. Fast estimates and quick turnaround.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-753" data-mp="3i165x0gvb249">
            <Button variant="secondary" size="lg" onClick={() => setPage('contact')} className="w-full sm:w-auto border-none text-brand-red" data-id="element-754" data-mp="345uccweg7hox">
              Request a Quote
            </Button>
            <Button variant="outline-red" size="lg" href="tel:5177778088" className="w-full sm:w-auto" data-id="element-755" data-mp="2z9n4gy2hrw4c">
              Call (517) 777-8088
            </Button>
          </div>
        </div>
      </section>
    </div>;
}